import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';

/**
 * 从本地相册选择图片或使用相机拍照接口请求参数
 */
export type FabricChooseImageOptions<T = number> = {
  /**
   * 最多可以选择的图片张数
   * @default 9
   */
  count?: number;

  /** 是否需要返回图片base64内容, 可指定尺寸 */
  resizedBase64?: null | {
    width: number;
    height: number;
  };

  /** 是否需要裁剪图片, 可指定裁剪比例 */
  crop?: null | {
    ratio: number;
  };

  /**
   * 图片质量, original: 原图、compressed: 压缩图、Number: (0-1]
   * @default 'original'
   */
  sizeType?: 'original' | 'compressed' | T;

  /**
   * 图片来源, album: 相册、camera: 相机、all: 相册 + 相机
   * @default 'album'
   */
  sourceType?: 'album' | 'camera' | 'all';
};

/**
 * 从本地相册选择图片或使用相机拍照, 结果返回.
 */
export type FabricChooseImageCallbackData = {
  /** 图片的本地临时文件列表 */
  tempFiles: Array<{
    /** 本地临时文件路径 */
    path: string;
    /** 配置 resizedBase64 时才返回 */
    base64?: string;
    /** 本地临时文件大小，单位 B */
    size: number;
  }>;
};

export type FabricPhotoChooseImageFileApi = (
  options?: FabricChooseImageOptions
) => Promise<FabricChooseImageCallbackData>;

/**
 * 从本地相册选择图片或使用相机拍照。
 */
export const chooseImage: FabricPhotoChooseImageFileApi = (
  options?: FabricChooseImageOptions
): Promise<FabricChooseImageCallbackData> => {
  return new Promise<FabricChooseImageCallbackData>((resolve, reject) => {
    core.call<
      'fabric_photo.chooseImage',
      FabricChooseImageOptions<string>,
      FabricChooseImageCallbackData
    >(
      'fabric_photo.chooseImage',
      {
        ...options,
        resizedBase64: options?.resizedBase64 ? options.resizedBase64 : null,
        crop: options?.crop ? options.crop : null,
        count: options?.count || 9,
        // Native 只支持字符串0-1, 优先判断`"original" | "compressed"`
        sizeType: options?.sizeType ? String(options.sizeType) : 'original',
      },
      (result) => {
        const fabricErrors = filterFabricErrors(
          result.code,
          result.message,
          result.data
        );
        if (fabricErrors) {
          reject(fabricErrors);
        } else {
          resolve(result.data);
        }
      }
    );
  });
};
